import React, { FC, useEffect, useState } from "react";
import { FormProvider, useForm } from "react-hook-form";
import { yupResolver } from "@hookform/resolvers/yup";
import { useTranslation } from "react-i18next";
import { GroupLabelModalPresenter } from "./GroupLabelModal.presenter";
import {
  GroupLabel,
  GroupLabelCreateRequestPayload,
  GroupLabelUpdateRequestPayload,
} from "@/domain/grouplabel.interface";
import {
  useGroupLabelCreateOne,
  useGroupLabelUpdateOne,
} from "@/hooks/useGroupLabels";
import { useFeedBack } from "@/providers/FeedBackProvider/FeedBackContext";
import { useCreateAuditlog } from "@/hooks/useAuditLogs";
import { useUserStore } from "@/hooks/useUserStore";
import { ACTION_CODE, MODULE_NAME, OPTIONS } from "@/domain/auditlog.enum";
import validationSchema, { GroupLabelFormData } from "./validation.schema";
import { PermissionDeniedModal } from "@/components/PermissionDeniedModal";
import { ErrorResponse } from "@/domain/error.interface";
import { GroupLabelStatusEnum } from "@/domain/grouplabel.interface";

export interface GroupLabelModalContainerProps {
  open: boolean;
  mode: "create" | "edit";
  groupLabel?: GroupLabel | null;
  onClose: () => void;
  onSuccess?: () => void;
  canAddGroupLabel: boolean;
  canEditGroupLabel: boolean;
}

export const GroupLabelModalContainer: FC<GroupLabelModalContainerProps> = ({
  open,
  mode,
  groupLabel,
  onClose,
  onSuccess,
  canAddGroupLabel,
  canEditGroupLabel,
}) => {
  const { t } = useTranslation("common");
  const { showSnackBar } = useFeedBack();
  const { userInfo } = useUserStore();
  const createAuditLog = useCreateAuditlog();

  // Mutations
  const groupLabelCreateOne = useGroupLabelCreateOne();
  const groupLabelUpdateOne = useGroupLabelUpdateOne();

  // State
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isPermissionDeniedModalOpen, setIsPermissionDeniedModalOpen] =
    useState(false);

  // Form setup
  const methods = useForm<GroupLabelFormData>({
    resolver: yupResolver(validationSchema(t)),
    mode: "all",
    defaultValues: {
      name: "",
      description: "",
      status: GroupLabelStatusEnum.ACTIVE,
    },
  });

  // Initialize form data when groupLabel is loaded (edit mode)
  useEffect(() => {
    if (mode === "edit" && groupLabel && open) {
      methods.reset({
        name: groupLabel.name,
        description: groupLabel.description,
        status: groupLabel.status,
      });
    } else if (mode === "create" && open) {
      methods.reset({
        name: "",
        description: "",
        status: GroupLabelStatusEnum.ACTIVE,
      });
    }
  }, [mode, groupLabel, open, methods]);

  // Handle submit
  const handleSubmit = async () => {
    if (mode === "create" && !canAddGroupLabel) {
      showSnackBar(t("generic_not_allowed") || "Not allowed", "error");
      return;
    }
    if (mode === "edit" && !canEditGroupLabel) {
      showSnackBar(t("generic_not_allowed") || "Not allowed", "error");
      return;
    }

    const isValid = await methods.trigger();
    if (!isValid) {
      return;
    }

    const formDataValues = methods.getValues();

    setIsSubmitting(true);
    try {
      if (mode === "create") {
        const payload: GroupLabelCreateRequestPayload = {
          name: formDataValues.name,
          description: formDataValues.description,
          status: formDataValues.status,
          createdBy: userInfo?._id || "",
        } as GroupLabelCreateRequestPayload;

        await groupLabelCreateOne.mutateAsync(payload);
        showSnackBar("Group Label created successfully", "success");

        createAuditLog.mutate({
          appType: "WEB_BACK_OFFICE",
          module: MODULE_NAME.GROUP_LABEL,
          option: OPTIONS.GROUP_LABEL_CREATE,
          actionCode: ACTION_CODE.WEB_GROUP_LABEL_CREATE,
          action: "When creating a new group label",
          detail: `Created group label ${formDataValues.name}`,
          transactionDate: new Date(),
          accountId: `${process.env.REACT_APP_ACCOUNT_ID}`,
          createdBy: userInfo?._id || "",
          name: `${userInfo?.firstName} ${userInfo?.lastName}`,
        });
      } else {
        const payload: GroupLabelUpdateRequestPayload = {
          ...formDataValues,
          _id: groupLabel?._id || "",
          updatedBy: userInfo?._id || "",
        } as GroupLabelUpdateRequestPayload;

        await groupLabelUpdateOne.mutateAsync({
          id: groupLabel?._id || "",
          data: payload,
        });
        showSnackBar("Group Label updated successfully", "success");

        createAuditLog.mutate({
          appType: "WEB_BACK_OFFICE",
          module: MODULE_NAME.GROUP_LABEL,
          option: OPTIONS.GROUP_LABEL_EDIT,
          actionCode: ACTION_CODE.WEB_GROUP_LABEL_EDIT,
          action: "When editing a group label",
          detail: `Edited group label ${formDataValues.name}`,
          transactionDate: new Date(),
          accountId: `${process.env.REACT_APP_ACCOUNT_ID}`,
          createdBy: userInfo?._id || "",
          name: `${userInfo?.firstName} ${userInfo?.lastName}`,
        });
      }

      onClose();
      if (onSuccess) {
        onSuccess();
      }
    } catch (error) {
      const err = error as ErrorResponse;
      const errorMessage = Array.isArray(err.message)
        ? err.message[0]
        : err.message || err.error || "An error occurred";

      showSnackBar(errorMessage, "error");
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <>
      <FormProvider {...methods}>
        <GroupLabelModalPresenter
          open={open}
          mode={mode}
          isSubmitting={
            isSubmitting ||
            groupLabelCreateOne.isPending ||
            groupLabelUpdateOne.isPending
          }
          isLoadingGroupLabelData={false}
          onClose={onClose}
          onSubmit={handleSubmit}
        />
      </FormProvider>

      {/* Permission Denied Modal */}
      <PermissionDeniedModal
        open={isPermissionDeniedModalOpen}
        onClose={() => setIsPermissionDeniedModalOpen(false)}
      />
    </>
  );
};
